import { EntityEventType } from '@racemap/sdk/schema/entityEvents';
import { RacemapColors } from '@racemap/utilities/consts/common';
import type { JSX } from 'react';
import {
  IconBatteryQuarter,
  IconBell,
  IconBurst,
  IconHeart,
  IconInfo,
  IconOffline,
  IconOnline,
  IconPerson,
  IconPowerOff,
  IconQuestion,
  IconReload,
  IconRoute,
  IconRunner,
} from 'src/components/BasicComponents/Icons';

type EntityEventTypeDisplayConfig = {
  color: string;
  secondaryColor: string;
  name: string;
  symbolSize: [number, number];
  symbolOffset: [number, string];
  icon: JSX.Element;
  symbol: string;
};

export const EntityEventTypeConfig: Partial<Record<EntityEventType, EntityEventTypeDisplayConfig>> =
  {
    [EntityEventType.SOS]: {
      color: RacemapColors.AltRed,
      secondaryColor: RacemapColors.LightLightRed,
      name: 'SOS',
      symbolSize: [13, 15],
      symbolOffset: [0, '-100%'],
      icon: <IconBell style={{ color: RacemapColors.AltRed }} />,
      symbol:
        'path://M224 0c-17.7 0-32 14.3-32 32l0 19.2C119 66 64 130.6 64 208l0 18.8c0 47-17.3 92.4-48.5 127.6l-7.4 8.3c-8.4 9.4-10.4 22.9-5.3 34.4S19.4 416 32 416l384 0c12.6 0 24-7.4 29.2-18.9s3.1-25-5.3-34.4l-7.4-8.3C401.3 319.2 384 273.9 384 226.8l0-18.8c0-77.4-55-142-128-156.8L256 32c0-17.7-14.3-32-32-32zm45.3 493.3c12-12 18.7-28.3 18.7-45.3l-64 0-64 0c0 17 6.7 33.3 18.7 45.3s28.3 18.7 45.3 18.7s33.3-6.7 45.3-18.7z',
    },
    [EntityEventType.NO_MOVEMENT]: {
      color: RacemapColors.LightOrange,
      secondaryColor: RacemapColors.LightLightOrange,
      name: 'No movement',
      symbolSize: [15, 15],
      symbolOffset: [0, '-100%'],
      icon: <IconPerson style={{ color: RacemapColors.LightOrange }} />,
      symbol:
        'path://M112 48a48 48 0 1 1 96 0 48 48 0 1 1 -96 0zm40 304l0 128c0 17.7-14.3 32-32 32s-32-14.3-32-32l0-223.1L59.4 304.5c-9.1 15.1-28.8 20-43.9 10.9s-20-28.8-10.9-43.9l58.3-97c17.4-28.9 48.6-46.6 82.3-46.6l29.7 0c33.7 0 64.9 17.7 82.3 46.6l58.3 97c9.1 15.1 4.2 34.8-10.9 43.9s-34.8 4.2-43.9-10.9L232 256.9 232 480c0 17.7-14.3 32-32 32s-32-14.3-32-32l0-128-16 0z',
    },
    [EntityEventType.MOVEMENT]: {
      color: RacemapColors.PaleBlue,
      secondaryColor: RacemapColors.LightBlue,
      name: 'Movement',
      symbolSize: [15, 15],
      symbolOffset: [0, '-100%'],
      icon: <IconRunner style={{ color: RacemapColors.PaleBlue }} />,
      symbol:
        'path://M320 48a48 48 0 1 0 -96 0 48 48 0 1 0 96 0zM125.7 175.5c9.9-9.9 23.4-15.5 37.5-15.5c1.9 0 3.8 .1 5.6 .3L137.6 254c-9.3 28 1.7 58.8 26.8 74.5l86.2 53.9-25.4 88.8c-4.9 17 5 34.7 22 39.6s34.7-5 39.6-22l28.7-100.4c5.9-20.6-2.6-42.6-20.7-53.9L238 299l30.9-82.4 5.1 12.3C289 264.7 323.9 288 362.7 288l21.3 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-21.3 0c-12.9 0-24.6-7.8-29.5-19.7l-6.3-15c-14.6-35.1-44.1-61.9-80.5-73.1l-48.7-15c-11.1-3.4-22.7-5.2-34.4-5.2c-31 0-60.8 12.3-82.7 34.3L57.4 153.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l23.1-23.1zM91.2 352L32 352c-17.7 0-32 14.3-32 32s14.3 32 32 32l69.6 0c19 0 36.2-11.2 43.9-28.5L157 361.6l-9.5-6c-17.5-10.9-30.5-26.8-37.9-44.9L91.2 352z',
    },
    [EntityEventType.AWAY_FROM_TRACK]: {
      color: RacemapColors.LightOrange,
      secondaryColor: RacemapColors.LightLightOrange,
      name: 'Away from track',
      symbolSize: [15, 15],
      symbolOffset: [0, '-100%'],
      icon: <IconRoute style={{ color: RacemapColors.LightOrange }} />,
      symbol:
        'path://M512 96c0 50.2-59.1 125.1-84.6 155c-3.8 4.4-9.4 6.1-14.5 5L320 256c-17.7 0-32 14.3-32 32s14.3 32 32 32l96 0c53 0 96 43 96 96s-43 96-96 96l-276.4 0c8.7-9.9 19.3-22.6 30-36.8c6.3-8.4 12.8-17.6 19-27.2L416 448c17.7 0 32-14.3 32-32s-14.3-32-32-32l-96 0c-53 0-96-43-96-96s43-96 96-96l39.8 0c-21-31.5-39.8-67.7-39.8-96c0-53 43-96 96-96s96 43 96 96zM117.1 489.1c-3.8 4.3-7.2 8.1-10.1 11.3l-1.8 2-.2-.2c-6 4.6-14.6 4-20-1.8C59.8 473 0 402.5 0 352c0-53 43-96 96-96s96 43 96 96c0 30-21.1 67-43.5 97.9c-10.7 14.7-21.7 28-30.8 38.5l-.6 .7zM128 352a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zM416 128a32 32 0 1 0 0-64 32 32 0 1 0 0 64z',
    },
    [EntityEventType.LOW_BATTERY]: {
      color: RacemapColors.PaleBlue,
      secondaryColor: RacemapColors.LightBlue,
      name: 'Low battery',
      symbolSize: [15, 10],
      symbolOffset: [0, '-150%'],
      icon: <IconBatteryQuarter style={{ color: RacemapColors.PaleBlue }} />,
      symbol:
        'path://M464 160c8.8 0 16 7.2 16 16l0 160c0 8.8-7.2 16-16 16L80 352c-8.8 0-16-7.2-16-16l0-160c0-8.8 7.2-16 16-16l384 0zM80 96C35.8 96 0 131.8 0 176L0 336c0 44.2 35.8 80 80 80l384 0c44.2 0 80-35.8 80-80l0-16c17.7 0 32-14.3 32-32l0-64c0-17.7-14.3-32-32-32l0-16c0-44.2-35.8-80-80-80L80 96zm112 96l-96 0 0 128 96 0 0-128z',
    },
    [EntityEventType.UNKNOWN]: {
      color: RacemapColors.Gray,
      secondaryColor: RacemapColors.LightLightGray,
      name: 'Unknown',
      symbolSize: [10, 15],
      symbolOffset: [0, '-100%'],
      icon: <IconQuestion style={{ color: RacemapColors.Gray }} />,
      symbol:
        'path://M80 160c0-35.3 28.7-64 64-64l32 0c35.3 0 64 28.7 64 64l0 3.6c0 21.8-11.1 42.1-29.4 53.8l-42.2 27.1c-25.2 16.2-40.4 44.1-40.4 74l0 1.4c0 17.7 14.3 32 32 32s32-14.3 32-32l0-1.4c0-8.2 4.2-15.8 11-20.2l42.2-27.1c36.6-23.6 58.8-64.1 58.8-107.7l0-3.6c0-70.7-57.3-128-128-128l-32 0C73.3 32 16 89.3 16 160c0 17.7 14.3 32 32 32s32-14.3 32-32zm80 320a40 40 0 1 0 0-80 40 40 0 1 0 0 80z',
    },
    [EntityEventType.POWER_OFF]: {
      color: RacemapColors.AltRed,
      secondaryColor: RacemapColors.LightLightRed,
      name: 'Power off',
      icon: <IconPowerOff style={{ color: RacemapColors.AltRed }} />,
      symbolSize: [15, 15],
      symbolOffset: [0, '-100%'],
      symbol:
        'path://M288 32c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 224c0 17.7 14.3 32 32 32s32-14.3 32-32l0-224zM143.5 120.6c13.6-11.3 15.4-31.5 4.1-45.1s-31.5-15.4-45.1-4.1C49.7 115.4 16 181.8 16 256c0 132.5 107.5 240 240 240s240-107.5 240-240c0-74.2-33.8-140.6-86.6-184.6c-13.6-11.3-33.8-9.4-45.1 4.1s-9.4 33.8 4.1 45.1c38.9 32.3 63.5 81 63.5 135.4c0 97.2-78.8 176-176 176s-176-78.8-176-176c0-54.4 24.7-103.1 63.5-135.4z',
    },
    [EntityEventType.CRASH]: {
      color: RacemapColors.AltRed,
      secondaryColor: RacemapColors.LightLightRed,
      name: 'Crash',
      icon: <IconBurst style={{ color: RacemapColors.AltRed }} />,
      symbolSize: [15, 15],
      symbolOffset: [0, '-100%'],
      symbol:
        'path://M37.6 4.2C28-2.3 15.2-1.1 7 7s-9.4 21-2.8 30.5l112 163.3L16.6 233.2C6.7 236.4 0 245.6 0 256s6.7 19.6 16.6 22.8l103.1 33.4L66.8 412.8c-4.9 9.3-3.2 20.7 4.3 28.1s18.8 9.2 28.1 4.3l100.6-52.9 33.4 103.1c3.2 9.9 12.4 16.6 22.8 16.6s19.6-6.7 22.8-16.6l33.4-103.1 100.6 52.9c9.3 4.9 20.7 3.2 28.1-4.3s9.2-18.8 4.3-28.1L392.3 312.2l103.1-33.4c9.9-3.2 16.6-12.4 16.6-22.8s-6.7-19.6-16.6-22.8L388.9 198.7l25.7-70.4c3.2-8.8 1-18.6-5.6-25.2s-16.4-8.8-25.2-5.6l-70.4 25.7L278.8 16.6C275.6 6.7 266.4 0 256 0s-19.6 6.7-22.8 16.6l-32.3 99.6L37.6 4.2z',
    },
    [EntityEventType.HEARTBEAT]: {
      color: RacemapColors.BaseGreen,
      secondaryColor: RacemapColors.LightLightGreen,
      name: 'Heartbeat',
      icon: <IconHeart style={{ color: RacemapColors.BaseGreen }} />,
      symbolSize: [15, 15],
      symbolOffset: [0, '-100%'],
      symbol:
        'path://M47.6 300.4L228.3 469.1c7.5 7 17.4 10.9 27.7 10.9s20.2-3.9 27.7-10.9L464.4 300.4c30.4-28.3 47.6-68 47.6-109.5v-5.8c0-69.9-50.5-129.5-119.4-141C347 36.5 300.6 51.4 268 84L256 96 244 84c-32.6-32.6-79-47.5-124.6-39.9C50.5 55.6 0 115.2 0 185.1v5.8c0 41.5 17.2 81.2 47.6 109.5z',
    },
    [EntityEventType.OFFLINE]: {
      color: RacemapColors.AltRed,
      secondaryColor: RacemapColors.LightLightRed,
      name: 'Offline',
      icon: <IconOffline style={{ color: RacemapColors.AltRed }} />,
      symbolSize: [15, 15],
      symbolOffset: [0, '-100%'],
      symbol:
        'path://M183.1 137.4C170.6 124.9 150.3 124.9 137.8 137.4C125.3 149.9 125.3 170.2 137.8 182.7L275.2 320L137.9 457.4C125.4 469.9 125.4 490.2 137.9 502.7C150.4 515.2 170.7 515.2 183.2 502.7L320.5 365.3L457.9 502.6C470.4 515.1 490.7 515.1 503.2 502.6C515.7 490.1 515.7 469.8 503.2 457.3L365.8 320L503.1 182.6C515.6 170.1 515.6 149.8 503.1 137.3C490.6 124.8 470.3 124.8 457.8 137.3L320.5 274.7L183.1 137.4z',
    },
    [EntityEventType.ONLINE]: {
      color: RacemapColors.BaseGreen,
      secondaryColor: RacemapColors.LightLightGreen,
      name: 'Online',
      icon: <IconOnline style={{ color: RacemapColors.BaseGreen }} />,
      symbolSize: [15, 15],
      symbolOffset: [0, '-100%'],
      symbol:
        'path://M160 96C124.7 96 96 124.7 96 160L96 224C96 259.3 124.7 288 160 288L480 288C515.3 288 544 259.3 544 224L544 160C544 124.7 515.3 96 480 96L160 96zM376 168C389.3 168 400 178.7 400 192C400 205.3 389.3 216 376 216C362.7 216 352 205.3 352 192C352 178.7 362.7 168 376 168zM432 192C432 178.7 442.7 168 456 168C469.3 168 480 178.7 480 192C480 205.3 469.3 216 456 216C442.7 216 432 205.3 432 192zM160 352C124.7 352 96 380.7 96 416L96 480C96 515.3 124.7 544 160 544L480 544C515.3 544 544 515.3 544 480L544 416C544 380.7 515.3 352 480 352L160 352zM376 424C389.3 424 400 434.7 400 448C400 461.3 389.3 472 376 472C362.7 472 352 461.3 352 448C352 434.7 362.7 424 376 424zM432 448C432 434.7 442.7 424 456 424C469.3 424 480 434.7 480 448C480 461.3 469.3 472 456 472C442.7 472 432 461.3 432 448z',
    },
    [EntityEventType.REBOOT]: {
      color: RacemapColors.LightOrange,
      secondaryColor: RacemapColors.LightLightOrange,
      name: 'Reboot',
      symbolSize: [15, 15],
      icon: <IconReload style={{ color: RacemapColors.LightOrange }} />,
      symbolOffset: [0, '-100%'],
      symbol:
        'path://M386.3 160L336 160c-17.7 0-32 14.3-32 32s14.3 32 32 32l128 0c17.7 0 32-14.3 32-32l0-128c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 51.2L414.4 97.6c-87.5-87.5-229.3-87.5-316.8 0s-87.5 229.3 0 316.8s229.3 87.5 316.8 0c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0c-62.5 62.5-163.8 62.5-226.3 0s-62.5-163.8 0-226.3s163.8-62.5 226.3 0L386.3 160z',
    },
    [EntityEventType.DEVICE_INFO]: {
      color: RacemapColors.PaleBlue,
      secondaryColor: RacemapColors.LightBlue,
      name: 'Device info',
      symbolSize: [15, 15],
      icon: <IconInfo style={{ color: RacemapColors.PaleBlue }} />,
      symbolOffset: [0, '-100%'],
      symbol:
        'path://M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM216 336l24 0 0-64-24 0c-13.3 0-24-10.7-24-24s10.7-24 24-24l48 0c13.3 0 24 10.7 24 24l0 88 8 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-80 0c-13.3 0-24-10.7-24-24s10.7-24 24-24zm40-208a32 32 0 1 1 0 64 32 32 0 1 1 0-64z',
    },
  };
